import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Project, ProjectStatus, ProjectStatusOption } from '../../types/project';
import { Button } from '../common/Button';
import CollapsibleContent from '../common/CollapsibleContent';
import BidderDetailsModal from '../quotes/BidderDetailsModal';
import { Quote, ChildQuote } from '../../types/quote';
import JobListCardHeader from '../jobs/JobListCardHeader';
import JobInfo from '../jobs/JobInfo';
import { computeQuoteTotalAmount } from '../../utils/jobUtils';
import { DropdownSelect } from '../common/DropdownSelect';
import ProjectJobDetailsModal from './ProjectJobDetailsModal';
import QuoteFreelancerCard from '../quotes/QuoteFreelancerCard';
import { useProjectStore } from '../../stores/projectStore';
import FullBlockingModal from '../common/FullBlockingModal';
import ReviewModal from '../reviews/ReviewModal';
import { useProjectStatusSelector } from '../../hooks/useProjectStatusSelector';

interface RecruiterProjectCardProps {
  project: Project;
  expanded?: boolean;
}

const RecruiterProjectCard: React.FC<RecruiterProjectCardProps> = ({
  project,
  expanded = false,
}) => {
  const { t } = useTranslation(['projects', 'common', 'hub']);

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [showStatusConfirmationModal, setShowStatusConfirmationModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [selectedQuote, setSelectedQuote] = useState<Quote | ChildQuote>();
  const [updateStatusError, setUpdateStatusError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const { updateProjectStatus } = useProjectStore();

  const {
    options: statusOptions,
    selectedOption: selectedStatus,
    setSelectedOption: setSelectedStatus,
  } = useProjectStatusSelector(project.quote.job.status as ProjectStatus);

  const statusHasNotChanged = selectedStatus.id === project.quote.job.status;

  const handleStatusUpdateClick = () => {
    setShowStatusConfirmationModal(true);
  };

  const handleConfirmConfirmationModal = () => {
    if (selectedStatus.id === 'completed') {
      setShowStatusConfirmationModal(false);
      setShowReviewModal(true);
    } else {
      executeStatusUpdate();
    }
  };

  const executeStatusUpdate = async () => {
    try {
      setIsUpdating(true);
      setUpdateStatusError(null);
      await updateProjectStatus(project.quote.job.id, selectedStatus.id);
      setShowStatusConfirmationModal(false);
      setShowReviewModal(false);
    } catch (error) {
      setUpdateStatusError(t('genericErrorUpdatingStatus', { ns: 'projects' }));
      console.error(error);
      setShowStatusConfirmationModal(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const resetStatusToOriginal = () => {
    const currentStatus = statusOptions.find(option => option.id === project.quote.job.status);
    if (currentStatus) setSelectedStatus(currentStatus);
  };

  const cancelStatusChange = () => {
    setShowStatusConfirmationModal(false);
    setUpdateStatusError(null);
    resetStatusToOriginal();
  };

  const grandTotal = computeQuoteTotalAmount(project.quote);
  const header = (
    <JobListCardHeader job={project.quote.job} grandTotal={grandTotal} projectMode={true} />
  );

  return (
    <>
      <CollapsibleContent
        className="flex flex-col rounded-2xl border-2 border-gray-300 bg-white p-4 shadow-xl transition-all duration-300"
        header={header}
        defaultExpanded={expanded}
        contentClassName="bg-white"
        expandButtonClassName="self-start"
      >
        <JobInfo job={project.quote.job} />
        <hr className="my-4 border-t border-gray-300" />

        <div className="flex flex-col gap-4 md:flex-row md:justify-between">
          <div className="flex flex-col gap-4">
            <h3 className="text-xl font-bold text-secondary">
              {t('myJobOffer', { ns: 'projects' })}
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                className="w-full md:w-auto md:px-8"
                href={`/projects/${project.quote.job.id}?selectedQuoteId=${project.quote.id}`}
              >
                {t('viewHub', { ns: 'hub', defaultValue: 'View details' })}
              </Button>
              <Button
                variant="transparent-with-border"
                className="w-full md:w-auto md:px-8"
                onClick={() => setShowJobDetailsModal(true)}
              >
                {t('viewMyJobOffer', { ns: 'projects' })}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-xl font-bold text-secondary">{t('projectStatus')}</h3>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
              <DropdownSelect<ProjectStatusOption>
                value={selectedStatus}
                onChange={(_, item) => item && setSelectedStatus(item)}
                options={statusOptions}
                getOptionLabel={status => status.label}
                getOptionValue={status => status.id}
              />
              <Button
                variant="primary"
                className="w-full md:w-auto md:px-8"
                onClick={handleStatusUpdateClick}
                disabled={statusHasNotChanged || isUpdating}
              >
                {t('changeStatus')}
              </Button>
            </div>
          </div>
        </div>

        <hr className="my-4 border-t border-gray-300" />

        <div className="flex flex-col gap-4 md:flex-row md:justify-between md:gap-8">
          <div className="flex flex-col gap-4">
            <h3 className="text-xl font-bold text-secondary">{t('biddingSubmissions')}</h3>
            <QuoteFreelancerCard
              freelancer={{
                ...project.quote.quote_owner,
                full_access: true,
                service_category_slugs: project.quote.quote_owner.services?.map(s => s.slug) || [],
              }}
              quote={project.quote}
              onClick={() => {
                setSelectedQuote(project.quote);
                setShowOfferModal(true);
              }}
            />
          </div>
          {/* equipes */}
          {project.quote.alliances.length > 0 && (
            <div className="flex flex-col gap-4">
              <h4 className="text-sm font-bold text-secondary">{t('team')}</h4>

              <div className="flex flex-col gap-4">
                {project.quote.alliances.map((alliance, index) => {
                  if (!alliance.child_quote) return null;

                  return (
                    <div key={alliance.id}>
                      <QuoteFreelancerCard
                        freelancer={{
                          id: alliance.child_quote.quote_owner.id,

                          first_name: alliance.child_quote.quote_owner.first_name,

                          last_name: alliance.child_quote.quote_owner.last_name,

                          avatar_url: alliance.child_quote.quote_owner.avatar_url || null,

                          service_category_slugs:
                            alliance.child_quote.quote_owner.services?.map(s => s.slug) || [],

                          full_access: true,
                        }}
                        quoteAlliance={alliance}
                        onClick={() => {
                          setSelectedQuote(alliance.child_quote!);

                          setShowOfferModal(true);
                        }}
                      />

                      {index !== project.quote.alliances.length - 1 && (
                        <hr className="my-4 border-t border-gray-300" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>

      {showStatusConfirmationModal && (
        <FullBlockingModal innerClassName="md:max-w-[32rem]">
          <div className="relative p-4 md:p-6">
            <h2 className="mb-4 text-2xl font-bold text-secondary">
              {t('statusChangeConfirmation')}
            </h2>
            <p className="mb-6 text-secondary">
              {t('statusChangeMessage')} <strong>{selectedStatus.label}</strong>.
            </p>
            {updateStatusError && (
              <div className="mb-4 text-sm font-bold text-red-600">{updateStatusError}</div>
            )}
            <div className="flex flex-col gap-4 md:flex-row-reverse">
              <Button
                onClick={handleConfirmConfirmationModal}
                variant="primary"
                disabled={isUpdating}
              >
                {isUpdating
                  ? t('loading', { ns: 'common' })
                  : t('common:form_actions.confirm', { ns: 'common' })}
              </Button>
              <Button onClick={cancelStatusChange} variant="transparent-with-border">
                {t('cancelAndClose', { ns: 'common' })}
              </Button>
            </div>
          </div>
        </FullBlockingModal>
      )}

      {showReviewModal && (
        <ReviewModal
          quote={project.quote}
          onClose={() => {
            setShowReviewModal(false);
            resetStatusToOriginal();
          }}
          onClick={() => {
            executeStatusUpdate();
          }}
        />
      )}

      {showOfferModal && selectedQuote && (
        <BidderDetailsModal
          onClose={() => setShowOfferModal(false)}
          job={project.quote.job}
          quote={selectedQuote}
        />
      )}
      {showJobDetailsModal && (
        <ProjectJobDetailsModal
          job={project.quote.job}
          onClose={() => setShowJobDetailsModal(false)}
        />
      )}
    </>
  );
};

export default RecruiterProjectCard;
